/* eslint-disable max-lines */
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Chip, Grid, MenuItem, Popover } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { difference, find, isEmpty, isNil, map } from 'lodash';
import { LoadingButton } from '@mui/lab';

import { OrderFillType, SortKey } from 'enums/common.enum';
import DateField from 'common/forms/DateField';
import TextField from 'common/forms/TextField';
import SelectField from 'common/forms/SelectField';
import { validateMinLength } from 'common/forms/formValidations';
import { filterPrescriptionsNeedsReview } from 'store/thunks/workQueueThunks';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import AutoCompleteMultipleSelect from 'common/forms/AutoCompleteMultipleSelect';
import { prescriptionReviewStatus } from 'common/constants/prescriptionStatus';
import { casingFormat } from 'common/utils';

import SYSTEM_TAG_NAMES from 'common/constants/systemTags';
import { tagMatchModeForFilter, tagMatchModeFormValue } from 'modules/WorkQueue/utils/tagMatchMode';
import FacilityFilterField from './FacilityFilterField';
import TagMatchModeField from './TagMatchModeField';

const PrescriptionReviewFilterButton = () => {
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [practices, setPractices] = useState(null);
  const [practiceLoading, setPracticeLoading] = useState(false);

  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [selectedPracticeIds, setSelectedPracticeIds] = useState([]);

  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedTagsNotIn, setSelectedTagsNotIn] = useState([]);
  const [disableTagsFilter, setDisableTagsFilter] = useState(false);
  const [disableTagsNotInFilter, setDisableTagsNotInFilter] = useState(false);

  const popoverId = anchorEl ? 'WorkQueue-PrescriptionReviewFilterButton-Popover' : undefined;

  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);
  const loading = useSelector((state) => state.workQueue.prescriptionReview.loading);
  const tagOptions = useSelector(({ systemConfigs }) => systemConfigs.lineItemTags || []);

  // Reverse of prescriptionReviewStatus (backend value -> display label), so the
  // selected label can be mapped straight back to its backend value on submit
  // instead of re-deriving it from the label's words (which breaks whenever a
  // label doesn't literally spell out its backend value, e.g. 'Drug review'
  // needs to resolve to 'DRUG_PROFILE_REVIEW').
  const reviewStatusValueByLabel = Object.fromEntries(
    Object.entries(prescriptionReviewStatus).map(([value, label]) => [label, value])
  );

  const formatTag = (tag) => casingFormat(tag?.replace(/_/g, ' '));

  const isSystemTagOption = (originalTagName) => SYSTEM_TAG_NAMES.includes(originalTagName);

  const sortedTagOptions = useMemo(() => {
    const visibleTags = tagOptions.filter((t) => t !== 'BACKFILLED_SHIP_BY');
    const userTags = visibleTags
      .filter((t) => !isSystemTagOption(t))
      .sort((a, b) => a.localeCompare(b));
    const systemTags = visibleTags
      .filter((t) => isSystemTagOption(t))
      .sort((a, b) => a.localeCompare(b));
    return [...userTags, ...systemTags];
  }, [tagOptions]);

  const renderTagOption = (props, option) => {
    // eslint-disable-next-line react/prop-types
    const { key, ...optionProps } = props;
    const originalTag = tagOptions.find((t) => formatTag(t) === option);
    const isSystem = originalTag ? isSystemTagOption(originalTag) : false;
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <li key={key} {...optionProps}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <span>{option}</span>
          {isSystem && (
            <Chip
              label='System'
              size='small'
              sx={{ ml: 1, height: 18, fontSize: '0.65rem', opacity: 0.7 }}
            />
          )}
        </Box>
      </li>
    );
  };

  const filter = useSelector(({ workQueue: { prescriptionReview } }) => ({
    ...prescriptionReview?.filter,
    requestedDate:
      prescriptionReview?.filter?.requestedDate &&
      new Date(prescriptionReview.filter.requestedDate),
    reviewStatus: prescriptionReview?.filter?.reviewStatus
      ? map(prescriptionReview?.filter?.reviewStatus, (status) => prescriptionReviewStatus[status])
      : [],
    tagMatchMode: tagMatchModeFormValue(prescriptionReview?.filter),
  }));

  const partner = useMemo(() => {
    return map(partners, ({ partnerName }) => casingFormat(partnerName));
  }, [partners]);

  const selectedPracticeNames = useMemo(() => {
    return selectedPracticeIds.map((practiceId) =>
      casingFormat(find(practices, { practiceId })?.practiceName)
    );
  }, [selectedPracticeIds, practices]);

  useEffect(() => {
    if (isEmpty(selectedPartnerIds)) {
      setPractices([]);
      setSelectedPractices([]);
      setSelectedPracticeIds([]);
      return;
    }
    setPracticeLoading(true);
    dispatch(getPartnerPractices({ partnerIds: selectedPartnerIds }))
      .then((practice) => {
        if (practice) {
          setPractices(practice);
        }
      })
      .finally(() => setPracticeLoading(false));
  }, [selectedPartnerIds, dispatch]);

  useEffect(() => {
    if (isEmpty(filter.partnerIds)) {
      setSelectedPartners([]);
      setSelectedPartnerIds([]);
    }
  }, [filter.partnerIds]);

  useEffect(() => {
    if (isEmpty(filter.practiceIds)) {
      setSelectedPractices([]);
      setSelectedPracticeIds([]);
    }
  }, [filter.practiceIds]);

  useEffect(() => {
    if (isEmpty(filter.tags)) {
      setSelectedTags([]);
    }
  }, [filter.tags]);

  useEffect(() => {
    if (isEmpty(filter.tagsNotIn)) {
      setSelectedTagsNotIn([]);
    }
  }, [filter.tagsNotIn]);

  useEffect(() => {
    const tagsEmpty = isEmpty(selectedTags);
    const tagsNotInEmpty = isEmpty(selectedTagsNotIn);

    setDisableTagsNotInFilter(!tagsEmpty);
    setDisableTagsFilter(!tagsNotInEmpty);
  }, [selectedTags, selectedTagsNotIn]);

  const handleOpen = (event) => {
    if (filter?.partnerIds) {
      setSelectedPartnerIds(filter.partnerIds);
      setSelectedPartners(
        filter.partnerIds.map((partnerId) =>
          casingFormat(find(partners, { partnerUuid: partnerId })?.partnerName)
        )
      );
    }

    if (filter?.practiceIds) {
      setSelectedPracticeIds(filter.practiceIds);
    }

    if (filter?.tags) {
      setSelectedTags(filter.tags);
    }

    if (filter?.tagsNotIn) {
      setSelectedTagsNotIn(filter.tagsNotIn);
    }

    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handlePartnerChange = (newValue) => {
    const partnerIds = newValue.map(
      (partnerName) =>
        partners.find((partnerData) => casingFormat(partnerData.partnerName) === partnerName)
          ?.partnerUuid
    );

    const removedPartnerIds = difference(selectedPartnerIds, partnerIds);

    setSelectedPartners(newValue);
    setSelectedPartnerIds(partnerIds);

    if (!isEmpty(removedPartnerIds)) {
      const practicesToRemove = practices.filter(
        (practice) =>
          removedPartnerIds.includes(practice.partnerId) &&
          selectedPracticeIds.includes(practice.practiceId)
      );

      const practiceNamesToRemove = practicesToRemove.map((practice) => practice.practiceName);
      const practiceIdsToRemove = practicesToRemove.map((practice) => practice.practiceId);

      setSelectedPractices((existingSelectedPartners) =>
        difference(existingSelectedPartners, practiceNamesToRemove)
      );

      setSelectedPracticeIds((existingSelectedPractice) =>
        difference(existingSelectedPractice, practiceIdsToRemove)
      );
    }
  };

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    dispatch(
      filterPrescriptionsNeedsReview({
        filter: {
          ...formData,
          requestedDate: formData.requestedDate && formData.requestedDate.toISOString(),
          reviewStatus: !isEmpty(formData.reviewStatus)
            ? formData.reviewStatus.map((value) => reviewStatusValueByLabel[value])
            : undefined,
          tags: !isEmpty(selectedTags) ? selectedTags : undefined,
          tagMatchMode: tagMatchModeForFilter(formData.tagMatchMode, selectedTags),
          tagsNotIn: !isEmpty(selectedTagsNotIn) ? selectedTagsNotIn : undefined,
          partnerIds: !isEmpty(selectedPartnerIds) ? selectedPartnerIds : undefined,
          practiceIds: !isEmpty(selectedPracticeIds) ? selectedPracticeIds : undefined,
          ...(!isNil(formData.isReshipment) && { isReshipment: formData.isReshipment }),
        },
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33, mr: 1 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '70vh',
              overflowY: 'auto',
            },
          },
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={filter}
              keepDirtyOnReinitialize
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid, values }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Order Id'
                      name={SortKey.ORDER_ID}
                      id='WorkQueue-PrescriptionReviewFilterButton-orderId-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <FacilityFilterField />
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Drug Name'
                      name={SortKey.DRUG_NAME}
                      id='WorkQueue-PrescriptionReviewFilterButton-drugName-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                      maxLength={300}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Requested Date'
                      name={SortKey.REQUESTED_DATE}
                      id='WorkQueue-PrescriptionReviewFilterButton-requestedDate-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Fill Type'
                      name={SortKey.FILL_TYPE}
                      id='WorkQueue-PrescriptionReviewFilterButton-fillType-dropdown'
                    >
                      <MenuItem value=''>All</MenuItem>
                      <MenuItem value={OrderFillType.FIRST_FILL}>First Fill</MenuItem>
                      <MenuItem value={OrderFillType.REFILL}>Refill</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Review Reason'
                      name='reviewStatus'
                      id='WorkQueue-PrescriptionReviewFilterButton-reviewStatus-dropdown'
                      multiple
                      options={Object.values(prescriptionReviewStatus)}
                      defaultValue={values?.reviewStatus}
                      sortOptions
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Partner'
                      name='partnerIds'
                      id='WorkQueue-PrescriptionReviewFilterButton-partnerIds-input'
                      multiple
                      defaultValue={selectedPartners}
                      options={partner}
                      sortOptions
                      value={selectedPartners}
                      onChange={(event, newValue) => handlePartnerChange(newValue)}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      key={selectedPartnerIds.length}
                      label='Practice'
                      name='practiceIds'
                      id='WorkQueue-PrescriptionReviewFilterButton-practiceIds-input'
                      multiple
                      defaultValue={selectedPracticeNames}
                      disabled={!selectedPartnerIds.length}
                      loading={practiceLoading}
                      options={practices.map((practice) => casingFormat(practice.practiceName))}
                      sortOptions
                      value={selectedPractices}
                      onChange={(event, newValue) => {
                        const practiceUuids = newValue.map(
                          (practiceName) =>
                            practices.find(
                              (practice) => casingFormat(practice.practiceName) === practiceName
                            )?.practiceId
                        );
                        setSelectedPractices(newValue);
                        setSelectedPracticeIds(practiceUuids);
                      }}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Tags'
                      name='tags'
                      id='WorkQueue-PrescriptionReviewFilterButton-tags-input'
                      multiple
                      defaultValue={selectedTags?.map(formatTag)}
                      value={selectedTags?.map(formatTag)}
                      options={sortedTagOptions.map(formatTag)}
                      disabled={disableTagsFilter}
                      formatOption={formatTag}
                      sortOptions={false}
                      renderOption={renderTagOption}
                      onChange={(event, newValue) => {
                        const originalTags = newValue.map((formattedTag) =>
                          tagOptions.find((originalTag) => formatTag(originalTag) === formattedTag)
                        );
                        setSelectedTags(originalTags);
                      }}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TagMatchModeField
                      id='WorkQueue-PrescriptionReviewFilterButton-tagMatchMode-dropdown'
                      disabled={isEmpty(selectedTags)}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Tags Not In'
                      name='tagsNotIn'
                      id='WorkQueue-PrescriptionReviewFilterButton-tagsNotIn-input'
                      multiple
                      defaultValue={selectedTagsNotIn?.map(formatTag)}
                      value={selectedTagsNotIn?.map(formatTag)}
                      options={sortedTagOptions.map(formatTag)}
                      disabled={disableTagsNotInFilter}
                      formatOption={formatTag}
                      sortOptions={false}
                      renderOption={renderTagOption}
                      onChange={(event, newValue) => {
                        const originalTags = newValue.map((formattedTag) =>
                          tagOptions.find((originalTag) => formatTag(originalTag) === formattedTag)
                        );
                        setSelectedTagsNotIn(originalTags);
                      }}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Reshipments'
                      name='isReshipment'
                      id='WorkQueue-PrescriptionReviewFilterButton-reshipment-dropdown'
                    >
                      <MenuItem value={undefined}>All</MenuItem>
                      <MenuItem value>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default PrescriptionReviewFilterButton;
